import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Package, CheckCircle, Clock, Truck, AlertCircle, Box, PackageCheck, Send, ChevronDown, ChevronUp } from 'lucide-react';

const TeacherKitRequests = () => {
  const [kitRequests, setKitRequests] = useState([]);
  const [filter, setFilter] = useState('all'); // all, pending, in_progress, delivered
  const [expandedRequests, setExpandedRequests] = useState(new Set());

  useEffect(() => {
    loadKitRequests();
  }, []);

  const toggleExpand = (requestId) => {
    setExpandedRequests(prev => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
      } else {
        newSet.add(requestId);
      }
      return newSet;
    });
  };

  const loadKitRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/kit-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKitRequests(response.data);
    } catch (error) {
      console.error('Failed to load kit requests', error);
      toast.error('Failed to load kit requests');
    }
  };

  const markAsReceived = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/kit-requests/${requestId}`,
        { status: 'delivered', admin_notes: '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Kit marked as received successfully');
      loadKitRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update kit status');
    }
  };

  const getStatusStage = (status) => {
    const stages = ['requested', 'approved', 'packed', 'in_transit', 'delivered'];
    const currentIndex = stages.indexOf(status);
    return currentIndex >= 0 ? currentIndex : 0;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderTimeline = (request) => {
    const stages = [
      { key: 'requested', label: 'Requested', icon: Send, color: 'blue' },
      { key: 'approved', label: 'Approved', icon: CheckCircle, color: 'green' },
      { key: 'packed', label: 'Packed', icon: Box, color: 'purple' },
      { key: 'in_transit', label: 'In Transit', icon: Truck, color: 'indigo' },
      { key: 'delivered', label: 'Delivered', icon: PackageCheck, color: 'emerald' }
    ];

    const currentStageIndex = getStatusStage(request.status);

    return (
      <div className="relative">
        {/* Timeline */}
        <div className="flex items-center justify-between mb-4">
          {stages.map((stage, index) => {
            const Icon = stage.icon;
            const isCompleted = index <= currentStageIndex;
            const isCurrent = index === currentStageIndex;
            
            return (
              <div key={stage.key} className="flex-1 relative">
                <div className="flex flex-col items-center">
                  {/* Icon */}
                  <div className={`
                    w-12 h-12 rounded-full flex items-center justify-center z-10 transition-all duration-300
                    ${isCompleted 
                      ? `bg-gradient-to-br from-${stage.color}-400 to-${stage.color}-600 shadow-lg` 
                      : 'bg-gray-200'
                    }
                    ${isCurrent ? 'ring-4 ring-' + stage.color + '-200 scale-110' : ''}
                  `}>
                    <Icon className={`w-6 h-6 ${isCompleted ? 'text-white' : 'text-gray-400'}`} />
                  </div>
                  
                  {/* Label */}
                  <p className={`mt-2 text-xs font-medium text-center ${
                    isCompleted ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {stage.label}
                  </p>
                  
                  {/* Date if available */}
                  {isCompleted && request[`${stage.key}_date`] && (
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(request[`${stage.key}_date`]).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                  )}
                </div>
                
                {/* Connecting Line */}
                {index < stages.length - 1 && (
                  <div className={`
                    absolute top-6 left-1/2 w-full h-1 -ml-6
                    ${index < currentStageIndex 
                      ? 'bg-gradient-to-r from-' + stage.color + '-500 to-' + stages[index + 1].color + '-500' 
                      : 'bg-gray-200'
                    }
                  `} style={{ zIndex: 0 }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'requested':
        return 'from-blue-50 to-blue-100 border-blue-200';
      case 'approved':
        return 'from-green-50 to-green-100 border-green-200';
      case 'packed':
        return 'from-purple-50 to-purple-100 border-purple-200';
      case 'in_transit':
        return 'from-indigo-50 to-indigo-100 border-indigo-200';
      case 'delivered':
        return 'from-emerald-50 to-emerald-100 border-emerald-200';
      default:
        return 'from-gray-50 to-gray-100 border-gray-200';
    }
  };

  const filteredRequests = kitRequests.filter(request => {
    if (filter === 'all') return true;
    if (filter === 'pending') return request.status === 'requested' || request.status === 'approved';
    if (filter === 'in_progress') return request.status === 'packed' || request.status === 'in_transit';
    if (filter === 'delivered') return request.status === 'delivered';
    return true;
  });

  const getFilterCount = (filterType) => {
    if (filterType === 'all') return kitRequests.length;
    if (filterType === 'pending') return kitRequests.filter(r => r.status === 'requested' || r.status === 'approved').length;
    if (filterType === 'in_progress') return kitRequests.filter(r => r.status === 'packed' || r.status === 'in_transit').length;
    if (filterType === 'delivered') return kitRequests.filter(r => r.status === 'delivered').length;
    return 0;
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 shadow-xl border-0 bg-gradient-to-br from-purple-50 via-white to-blue-50">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg">
              <Package className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                My Kit Requests
              </h3>
              <p className="text-sm text-gray-600 mt-1">Track your student kit requests with real-time status</p>
            </div>
          </div>

          {/* Filter Buttons */}
          <div className="flex items-center gap-2 bg-white rounded-lg p-1 shadow-sm border">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                filter === 'all' 
                  ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-md' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              All ({getFilterCount('all')})
            </button>
            <button
              onClick={() => setFilter('pending')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                filter === 'pending' 
                  ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white shadow-md' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Pending ({getFilterCount('pending')})
            </button>
            <button
              onClick={() => setFilter('in_progress')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                filter === 'in_progress' 
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              In Progress ({getFilterCount('in_progress')})
            </button>
            <button
              onClick={() => setFilter('delivered')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                filter === 'delivered' 
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-md' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Delivered ({getFilterCount('delivered')})
            </button>
          </div>
        </div>

        {/* Kit Requests List */}
        <div className="space-y-6">
          {filteredRequests.length === 0 ? (
            <div className="text-center py-16">
              <div className="inline-flex p-4 bg-gray-100 rounded-full mb-4">
                <Package className="w-12 h-12 text-gray-400" />
              </div>
              <p className="text-gray-600 text-lg font-medium">No kit requests found</p>
              <p className="text-gray-500 text-sm mt-1">
                {filter !== 'all' ? `No ${filter.replace('_', ' ')} requests at the moment` : 'Start by creating a student and requesting a kit'}
              </p>
            </div>
          ) : (
            filteredRequests.map(request => {
              const isExpanded = expandedRequests.has(request.id);
              const currentStageIndex = getStatusStage(request.status);
              const stages = ['requested', 'approved', 'packed', 'in_transit', 'delivered'];
              
              return (
                <Card key={request.id} className={`p-5 border-2 hover:shadow-xl transition-all duration-300 bg-gradient-to-br ${getStatusColor(request.status)}`}>
                  {/* Compact Header - Always Visible */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                        {request.student_name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-gray-900">{request.student_name}</h4>
                        <p className="text-sm text-gray-600">
                          Center: <span className="font-semibold">{request.center_name}</span>
                          {request.kit_amount && (
                            <span className="ml-3 font-bold text-purple-700">₹{request.kit_amount}</span>
                          )}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                            request.status === 'requested' ? 'bg-blue-100 text-blue-700' :
                            request.status === 'approved' ? 'bg-green-100 text-green-700' :
                            request.status === 'packed' ? 'bg-purple-100 text-purple-700' :
                            request.status === 'in_transit' ? 'bg-indigo-100 text-indigo-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}>
                            {currentStageIndex + 1}/5 - {request.status.replace('_', ' ').toUpperCase()}
                          </span>
                          {request.payment_status && (
                            <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                              request.payment_status === 'paid' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-orange-100 text-orange-700'
                            }`}>
                              {request.payment_status.toUpperCase()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {request.status === 'in_transit' && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsReceived(request.id);
                          }}
                          className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg"
                          size="sm"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Mark Received
                        </Button>
                      )}
                      {request.status === 'delivered' && (
                        <div className="flex items-center gap-2 text-emerald-600 font-bold bg-emerald-50 px-3 py-2 rounded-lg border-2 border-emerald-200">
                          <CheckCircle className="w-4 h-4" />
                          Delivered
                        </div>
                      )}
                      <Button
                        onClick={() => toggleExpand(request.id)}
                        variant="outline"
                        size="sm"
                        className="ml-2"
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp className="w-4 h-4 mr-1" />
                            Hide Details
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4 mr-1" />
                            View Progress
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Content - Only Visible When Clicked */}
                  {isExpanded && (
                    <div className="mt-6 pt-6 border-t-2 border-gray-200 animate-in fade-in duration-300">
                      {/* Timeline */}
                      {renderTimeline(request)}

                      {/* Additional Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                        <div className="bg-white/70 p-4 rounded-lg border border-gray-200">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">Request Date</p>
                          <p className="text-sm font-bold text-gray-900">{formatDate(request.requested_date || request.created_at)}</p>
                        </div>
                        {request.delivered_date && (
                          <div className="bg-white/70 p-4 rounded-lg border border-gray-200">
                            <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">Delivery Date</p>
                            <p className="text-sm font-bold text-gray-900">{formatDate(request.delivered_date)}</p>
                          </div>
                        )}
                      </div>

                      {/* Notes */}
                      {request.request_notes && (
                        <div className="mt-4 p-4 bg-white/80 rounded-lg border border-gray-200">
                          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-2">Request Notes:</p>
                          <p className="text-sm text-gray-800">{request.request_notes}</p>
                        </div>
                      )}

                      {request.admin_notes && (
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide mb-2">Admin Notes:</p>
                          <p className="text-sm text-blue-900">{request.admin_notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })
          )}
        </div>
      </Card>
    </div>
  );
};

export default TeacherKitRequests;
